import { useState, useEffect } from "react";
import { JobApplicationType } from "types/careers-types";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import { Link, useNavigate } from "react-router-dom";
import { MdVisibility, MdOutlinePages } from "react-icons/md";
import { Lock, Video, MessageSquare } from "lucide-react";
import Table from "components/Table";
import SelectField from "components/SelectField";
import Card from "components/Card";
import RejectionConfirmationModal from "components/RejectionConfirmationModal";
import {
  createTestSessionForApplication,
  createVideoTestSessionForApplication,
  createGeneralQuestionnaireSessionForApplication,
  updateJobApplicationStatus,
} from "api/apiHelper";
import { toast } from "react-toastify";

interface JobApplicationsTableProps {
  jobApplications: JobApplicationType[] | [];
  onRefresh: () => void;
  searchTerm?: string;
}

const JOB_STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "New", value: "new" },
  { label: "In Review", value: "in-review" },
  { label: "Shortlisted", value: "shortlisted" },
  { label: "Interviewed", value: "interviewed" },
  { label: "Offered", value: "offered" },
  { label: "Rejected", value: "rejected" },
];

const STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "In Review", value: "in-review" },
  { label: "Shortlisted", value: "shortlisted" },
  { label: "Interviewed", value: "interviewed" },
  { label: "Offered", value: "offered" },
  { label: "Rejected", value: "rejected" },
];

const JobApplicationsTable = (props: JobApplicationsTableProps) => {
  const { jobApplications, onRefresh, searchTerm = "" } = props;
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedJobOpening, setSelectedJobOpening] = useState<string>("all");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [testScoreValue, setTestScoreValue] = useState<string>("");
  const [testScoreOperator, setTestScoreOperator] = useState<string>(">");
  const [filteredApplications, setFilteredApplications] =
    useState<JobApplicationType[]>(jobApplications);
  const [processingTestId, setProcessingTestId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [rejectionModal, setRejectionModal] = useState<{
    isOpen: boolean;
    applicationId: string | null;
    applicantName: string;
    jobTitle: string;
    currentStatus: string;
  }>({
    isOpen: false,
    applicationId: null,
    applicantName: "",
    jobTitle: "",
    currentStatus: "",
  });

  const getEffectiveStatus = (session: any): string | undefined => {
    if (!session) return undefined;
    return session.effective_status || session.status;
  };

  /** Determine which test(s) can be resent and return the action to take */
  const getResendAction = (application: JobApplicationType): "first_test" | "video_test" | "questionnaire" | null => {
    const testSession = application.test_session;
    const videoTestSession = application.video_test_session;
    const questionnaireSession = application.general_questionnaire_session;

    const firstStatus = getEffectiveStatus(testSession);
    const videoStatus = getEffectiveStatus(videoTestSession);
    const questionnaireStatus = getEffectiveStatus(questionnaireSession);

    // If first test is pending or in progress, nothing to resend
    if (testSession && (firstStatus === "pending" || firstStatus === "in_progress")) {
      return null;
    }

    // If all three are completed, nothing to resend
    if (firstStatus === "completed" && videoStatus === "completed" && questionnaireStatus === "completed") {
      return null;
    }

    // First test expired or no first test → resend whole flow (first test)
    if (!testSession || firstStatus === "expired") {
      return "first_test";
    }

    // First test completed, video test expired or missing → resend video test (and questionnaire will be created on completion)
    if (firstStatus === "completed" && (!videoTestSession || videoStatus === "expired")) {
      return "video_test";
    }

    // First test and video test completed, questionnaire expired or missing → resend questionnaire only
    if (
      firstStatus === "completed" &&
      videoStatus === "completed" &&
      (!questionnaireSession || questionnaireStatus === "expired")
    ) {
      return "questionnaire";
    }

    return null;
  };

  const handleSendTest = async (applicationId: string, application: JobApplicationType) => {
    const action = getResendAction(application);

    if (!action) {
      if (application.test_session && getEffectiveStatus(application.test_session) === "pending") {
        toast.error("Applicant already has a valid pending test");
      } else if (
        getEffectiveStatus(application.test_session) === "completed" &&
        getEffectiveStatus(application.video_test_session) === "completed" &&
        getEffectiveStatus(application.general_questionnaire_session) === "completed"
      ) {
        toast.error("Applicant has already submitted the complete test");
      } else {
        toast.error("No test can be resent at this time");
      }
      return;
    }

    setProcessingTestId(applicationId);
    const actionLabels = {
      first_test: "Sending assessment...",
      video_test: "Sending video test...",
      questionnaire: "Sending questionnaire...",
    };
    const successLabels = {
      first_test: "Assessment sent successfully",
      video_test: "Video test sent successfully",
      questionnaire: "Questionnaire sent successfully",
    };
    const notification = toast.loading(actionLabels[action]);

    try {
      if (action === "first_test") {
        await createTestSessionForApplication(applicationId);
      } else if (action === "video_test") {
        await createVideoTestSessionForApplication(applicationId);
      } else {
        await createGeneralQuestionnaireSessionForApplication(applicationId);
      }
      toast.update(notification, {
        render: successLabels[action],
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      onRefresh();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to send test";
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingTestId(null);
    }
  };

  const handleStatusUpdate = async (applicationId: string, newStatus: string) => {
    setUpdatingStatusId(applicationId);
    const notification = toast.loading("Updating status...");

    try {
      await updateJobApplicationStatus(applicationId, newStatus);
      toast.update(notification, {
        render: newStatus === "rejected" 
          ? "Status updated and rejection email sent successfully"
          : "Status updated successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      onRefresh();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || "Failed to update status";
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleRejectionConfirm = async () => {
    if (rejectionModal.applicationId) {
      await handleStatusUpdate(rejectionModal.applicationId, "rejected");
      setRejectionModal({
        isOpen: false,
        applicationId: null,
        applicantName: "",
        jobTitle: "",
        currentStatus: "",
      });
    }
  };

  const handleRejectionCancel = () => {
    setRejectionModal({
      isOpen: false,
      applicationId: null,
      applicantName: "",
      jobTitle: "",
      currentStatus: "",
    });
  };

  // Create unique job openings map using slug as key
  const uniqueJobOpeningsMap = new Map<string, { slug: string; name: string }>();
  
  jobApplications.forEach((app) => {
    const jobOpening = app.job_opening;
    let slug = "";
    let name = app.job_opening_name || "";
    
    // Get slug from job_opening
    if (typeof jobOpening === 'string') {
      slug = jobOpening;
      // If no name from job_opening_name, format the slug
      if (!name) {
        name = slug
          .split('-')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
    } else if (jobOpening && typeof jobOpening === 'object') {
      slug = jobOpening.slug || jobOpening.id || "";
      name = name || jobOpening.title || slug;
    }
    
    if (slug && !uniqueJobOpeningsMap.has(slug)) {
      uniqueJobOpeningsMap.set(slug, { slug, name });
    }
  });
  
  const jobOpeningOptions = [
    { label: "All", value: "all" },
    ...Array.from(uniqueJobOpeningsMap.values()).map((jo) => ({
      label: jo.name,
      value: jo.slug,
    })),
  ];

  useEffect(() => {
    console.log(jobApplications, "Job Applications Data");
  }, [jobApplications]);

  useEffect(() => {
    const filteredData = jobApplications.filter((application) => {
      const statusMatch =
        selectedStatus === "all" || application.status === selectedStatus;
      const jobOpening = application.job_opening;
      // Prioritize slug over id for matching (consistent with route expectations)
      const jobOpeningIdentifier = typeof jobOpening === 'string' 
        ? jobOpening 
        : (jobOpening?.slug || jobOpening?.id || "");
      const jobOpeningMatch =
        selectedJobOpening === "all" ||
        jobOpeningIdentifier === selectedJobOpening;
      
      // Apply search filter
      let searchMatch = true;
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase().trim();
        const applicantName = application.applicant?.name?.toLowerCase() || "";
        const applicantEmail = application.applicant?.email?.toLowerCase() || "";
        const jobOpeningName = application.job_opening_name?.toLowerCase() || "";
        const status = application.status?.toLowerCase() || "";
        const appliedAt = application.applied_at?.toString().toLowerCase() || "";
        const notes = application.notes?.toLowerCase() || "";
        
        searchMatch = 
          applicantName.includes(searchLower) ||
          applicantEmail.includes(searchLower) ||
          jobOpeningName.includes(searchLower) ||
          status.includes(searchLower) ||
          appliedAt.includes(searchLower) ||
          notes.includes(searchLower) ||
          application.id?.toString().includes(searchLower);
      }
      
      // Apply date range filter
      let dateMatch = true;
      if (startDate || endDate) {
        const appliedDate = application.applied_at ? new Date(application.applied_at) : null;
        if (appliedDate) {
          // Set time to start of day for accurate comparison
          const appliedDateOnly = new Date(appliedDate.getFullYear(), appliedDate.getMonth(), appliedDate.getDate());
          
          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            dateMatch = appliedDateOnly >= start && appliedDateOnly <= end;
          } else if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            dateMatch = appliedDateOnly >= start;
          } else if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            dateMatch = appliedDateOnly <= end;
          }
        } else {
          dateMatch = false; // If no applied_at date, exclude from results when date filter is active
        }
      }
      
      // Apply test score filter
      let testScoreMatch = true;
      if (testScoreValue.trim()) {
        const testSession = application.test_session;
        if (testSession && testSession.status === 'completed' && testSession.score_percentage !== null) {
          // Both values are percentages - compare percentage with percentage
          const score = Number(testSession.score_percentage); // Convert to number (percentage value)
          const filterValue = Number(testScoreValue); // Convert user input to number (percentage)
          
          // Perform direct comparisons on percentage values
          if (isNaN(score) || isNaN(filterValue)) {
            testScoreMatch = false;
          } else {
            switch (testScoreOperator) {
              case ">":
                testScoreMatch = score > filterValue;
                break;
              case ">=":
                testScoreMatch = score >= filterValue;
                break;
              case "<":
                testScoreMatch = score < filterValue;
                break;
              case "<=":
                testScoreMatch = score <= filterValue;
                break;
              case "=":
                testScoreMatch = score === filterValue;
                break;
              default:
                testScoreMatch = true;
            }
          }
        } else {
          // If no test session or not completed, exclude from results when test score filter is active
          testScoreMatch = false;
        }
      }
      
      return statusMatch && jobOpeningMatch && searchMatch && dateMatch && testScoreMatch;
    });
    setFilteredApplications(filteredData);
  }, [selectedStatus, selectedJobOpening, jobApplications, searchTerm, startDate, endDate, testScoreValue, testScoreOperator]);

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(event.target.value);
  };

  const handleJobOpeningChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedJobOpening(event.target.value);
  };

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value);
  };

  const handleClearDateFilter = () => {
    setStartDate("");
    setEndDate("");
  };

  const handleTestScoreValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setTestScoreValue(value);
    }
  };

  const handleTestScoreOperatorChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTestScoreOperator(event.target.value);
  };

  const handleClearTestScoreFilter = () => {
    setTestScoreValue("");
    setTestScoreOperator(">");
  };

  const columnHelper = createColumnHelper<JobApplicationType>();
  const columns: ColumnDef<JobApplicationType, any>[] = [
    columnHelper.display({
      id: "job_opening",
      header: () => <TableHeader>VIEW JOB OPENING</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        // Use job_opening_name if available (from serializer), otherwise fallback to job_opening
        const title = application.job_opening_name;
        const jobOpening = application.job_opening;
        
        // Get slug from job_opening (can be string slug or object with slug)
        let jobSlug: string = "";
        if (typeof jobOpening === 'string') {
          jobSlug = jobOpening;
        } else if (jobOpening && typeof jobOpening === 'object') {
          jobSlug = jobOpening.slug || jobOpening.id || "";
        }
        
        // If we have a title from job_opening_name, use it
        if (title) {
          if (!jobSlug) {
            return <TableCell>{title}</TableCell>;
          }
          return (
            <Link 
              to={`/careers/job/${jobSlug}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline text-blue-600 dark:text-blue-400"
            >
              <TableCell>{title}</TableCell>
            </Link>
          );
        }
        
        // Fallback: If no title but we have slug, format and display it
        if (jobSlug) {
          const displayText = jobSlug
            .split('-')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          return (
            <Link 
              to={`/careers/job/${jobSlug}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline text-blue-600 dark:text-blue-400"
            >
              <TableCell>{displayText}</TableCell>
            </Link>
          );
        }
        
        // No job opening data
        return <TableCell>-</TableCell>;
      },
    }),
    // columnHelper.accessor("applicant.id", {
    //   id: "applicant.id",
    //   header: () => <TableHeader>APPLICANT ID</TableHeader>,
    //   cell: (info) => (
    //     <Link to={`/careers/applicant/${info.getValue()}`}>
    //       <TableCell>
    //         <MdVisibility className="h-6 w-6 text-gray-600" />
    //       </TableCell>
    //     </Link>
    //   ),
    // }),
    columnHelper.accessor("applicant.name", {
      id: "applicant.name",
      header: () => <TableHeader>APPLICANT NAME</TableHeader>,
      cell: (info) => {
        const applicant = info.row.original.applicant;
        const applicantId = applicant.id;
        return (
          <Link 
            to={`/careers/applicant/${applicantId}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:underline"
          >
            <TableCell>{info.getValue()}</TableCell>
          </Link>
        );
      },
    }),
    columnHelper.accessor("resume", {
      id: "resume",
      header: () => <TableHeader>RESUME</TableHeader>,
      cell: (info) => (
        <TableCell>
          <a 
            href={info.getValue()} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center"
          >
            <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
              <MdVisibility className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
          </a>
        </TableCell>
      ),
    }),
    columnHelper.accessor("applied_at", {
      id: "applied_at",
      header: () => <TableHeader>APPLIED AT</TableHeader>,
      cell: (info) => {
        const date = new Date(info.getValue());
        const formattedDate = date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return <TableCell>{formattedDate}</TableCell>;
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <TableHeader>STATUS</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        const status = info.getValue();
        const applicationId = application.id;
        const isUpdating = updatingStatusId === applicationId;

        const getStatusStyle = (status: string) => {
          switch (status) {
            case "new":
              return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
            case "in-review":
              return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
            case "shortlisted":
              return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700";
            case "interviewed":
              return "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700";
            case "offered":
              return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
            case "rejected":
              return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
            default:
              return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600";
          }
        };

        const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          e.stopPropagation();
          const newStatus = e.target.value;
          if (applicationId && newStatus !== status) {
            // Show confirmation modal if rejecting
            if (newStatus === "rejected") {
              const applicantName = application.applicant?.name || "Applicant";
              const jobTitle = application.job_opening_name || "Position";
              setRejectionModal({
                isOpen: true,
                applicationId: applicationId,
                applicantName: applicantName,
                jobTitle: jobTitle,
                currentStatus: status,
              });
              // Reset dropdown to current status
              e.target.value = status;
            } else {
              // For other status changes, update directly
              handleStatusUpdate(applicationId, newStatus);
            }
          }
        };

        // Get arrow color based on status for better visibility
        const getArrowColor = (status: string) => {
          switch (status) {
            case "new":
              return "%233b82f6"; // blue-500
            case "in-review":
              return "%23eab308"; // yellow-500
            case "shortlisted":
              return "%23a855f7"; // purple-500
            case "interviewed":
              return "%234f46e5"; // indigo-500
            case "offered":
              return "%2316a34a"; // green-600
            case "rejected":
              return "%23dc2626"; // red-600
            default:
              return "%236b7280"; // gray-500
          }
        };

        return (
          <TableCell>
            <div className="inline-flex items-center gap-2">
              <select
                value={status}
                onChange={handleStatusChange}
                disabled={isUpdating || !applicationId}
                onClick={(e) => e.stopPropagation()}
                className={`
                  inline-flex items-center rounded-full border px-2.5 py-1 pr-8 text-xs font-semibold
                  cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90
                  ${getStatusStyle(status)}
                `}
                style={{ 
                  appearance: 'none',
                  backgroundImage: isUpdating 
                    ? 'none'
                    : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${getArrowColor(status)}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {isUpdating && (
                <svg className="animate-spin h-3 w-3 text-gray-600 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          </TableCell>
        );
      },
    }),
    columnHelper.accessor("test_session", {
      id: "test_session",
      header: () => <TableHeader>FIRST TEST</TableHeader>,
      cell: (info) => {
        const testSession = info.getValue();
        if (!testSession) {
          return (
            <TableCell>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                No Test
              </span>
            </TableCell>
          );
        }

        const { test_token, score_percentage } = testSession;
        const status = getEffectiveStatus(testSession);

        if (status === "completed") {
          const score =
            score_percentage !== null ? Number(score_percentage) : 0;
          const scoreColor =
            score >= 70
              ? "bg-green-100 text-green-700 border-green-200"
              : score >= 50
              ? "bg-yellow-100 text-yellow-700 border-yellow-200"
              : "bg-red-100 text-red-700 border-red-200";

          return (
            <Link 
              to={`/careers/job-application/first-test-result/${test_token}`}
              onClick={(e) => e.stopPropagation()}
            >
              <TableCell className="!w-max !min-w-0">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${scoreColor} transition-opacity hover:opacity-80`}
                >
                  <MdOutlinePages className="h-4 w-4" />
                  {score_percentage !== null ? `${score.toFixed(0)}%` : "View"}
                </span>
              </TableCell>
            </Link>
          );
        } else if (status === "in_progress") {
          return (
            <TableCell>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-700">
                <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500"></span>
                In Progress
              </span>
            </TableCell>
          );
        } else if (status === "pending") {
          return (
            <TableCell>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-100 px-3 py-1.5 text-xs font-semibold text-blue-700">
                <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                Pending
              </span>
            </TableCell>
          );
        } else if (status === "expired") {
          return (
            <TableCell>
              <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600">
                Expired
              </span>
            </TableCell>
          );
        } else {
          return (
            <TableCell>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                {status || testSession.status}
              </span>
            </TableCell>
          );
        }
      },
    }),
    columnHelper.display({
      id: "video_test_status",
      header: () => <TableHeader>VIDEO TEST</TableHeader>,
      cell: ({ row }) => {
        const testSession = row.original.test_session;
        const videoTestSession = row.original.video_test_session;

        if (!videoTestSession) {
          // Unified flow: video test is "locked" until the first test is completed.
          const isVideoLocked =
            !!testSession && (() => {
              const s = getEffectiveStatus(testSession);
              return s === "pending" || s === "in_progress";
            })();

          return (
            <TableCell>
              {isVideoLocked ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300">
                  <Lock className="h-3.5 w-3.5" />
                  Locked
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                  Not Assigned
                </span>
              )}
            </TableCell>
          );
        }

        const getVideoTestStatusStyle = (status: string) => {
          switch (status) {
            case "pending":
              return "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200";
            case "in_progress":
              return "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200";
            case "completed":
              return "bg-green-100 text-green-700 border-green-200 hover:bg-green-200";
            case "expired":
              return "bg-red-100 text-red-700 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200";
          }
        };

        const getVideoTestStatusLabel = (status: string) => {
          switch (status) {
            case "pending":
              return "Pending";
            case "in_progress":
              return "In Progress";
            case "completed":
              return "Completed";
            case "expired":
              return "Expired";
            default:
              return status;
          }
        };

        const videoStatus = getEffectiveStatus(videoTestSession);
        const shouldRedirect = videoStatus !== "pending" && videoStatus !== "expired";

        const buttonContent = (
          <button
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              shouldRedirect ? "cursor-pointer" : "cursor-default"
            } ${getVideoTestStatusStyle(videoStatus || videoTestSession.status)}`}
            disabled={!shouldRedirect}
            onClick={(e) => e.stopPropagation()}
          >
            <Video className="h-3.5 w-3.5" />
            {getVideoTestStatusLabel(videoStatus || videoTestSession.status)}
          </button>
        );

        return (
          <TableCell>
            {shouldRedirect ? (
              <Link 
                to={`/careers/job-application/video-test-result/${videoTestSession.test_token}`}
                onClick={(e) => e.stopPropagation()}
              >
                {buttonContent}
              </Link>
            ) : (
              buttonContent
            )}
          </TableCell>
        );
      },
    }),
    columnHelper.display({
      id: "general_questionnaire_status",
      header: () => <TableHeader>GENERAL QUESTIONNAIRE</TableHeader>,
      cell: ({ row }) => {
        const testSession = row.original.test_session;
        const videoTestSession = row.original.video_test_session;
        const questionnaireSession = row.original.general_questionnaire_session;

        if (!questionnaireSession) {
          // Unified flow: questionnaire is "locked" until the video test is completed.
          const isQuestionnaireLocked =
            !!testSession && (!videoTestSession || getEffectiveStatus(videoTestSession) !== "completed");

          return (
            <TableCell>
              {isQuestionnaireLocked ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300">
                  <Lock className="h-3.5 w-3.5" />
                  Locked
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
                  Not Assigned
                </span>
              )}
            </TableCell>
          );
        }

        const getQuestionnaireStatusStyle = (status: string) => {
          switch (status) {
            case "pending":
              return "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200";
            case "in_progress":
              return "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200";
            case "completed":
              return "bg-green-100 text-green-700 border-green-200 hover:bg-green-200";
            case "expired":
              return "bg-red-100 text-red-700 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200";
          }
        };

        const getQuestionnaireStatusLabel = (status: string) => {
          switch (status) {
            case "pending":
              return "Pending";
            case "in_progress":
              return "In Progress";
            case "completed":
              return "Completed";
            case "expired":
              return "Expired";
            default:
              return status;
          }
        };

        const questionnaireStatus = getEffectiveStatus(questionnaireSession);
        const shouldRedirect = questionnaireStatus === "completed";

        const buttonContent = (
          <button
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              shouldRedirect ? "cursor-pointer" : "cursor-default"
            } ${getQuestionnaireStatusStyle(questionnaireStatus || questionnaireSession.status)}`}
            disabled={!shouldRedirect}
            onClick={(e) => e.stopPropagation()}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            {getQuestionnaireStatusLabel(questionnaireStatus || questionnaireSession.status)}
          </button>
        );

        return (
          <TableCell>
            {shouldRedirect ? (
              <Link 
                to={`/careers/job-application/general-questionnaire-result/${questionnaireSession.test_token}`}
                onClick={(e) => e.stopPropagation()}
              >
                {buttonContent}
              </Link>
            ) : (
              buttonContent
            )}
          </TableCell>
        );
      },
    }),

    columnHelper.display({
      id: "send_test",
      header: () => <TableHeader>SEND TEST</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        const isProcessing = processingTestId === application.id;
        const canResend = getResendAction(application) !== null;

        return (
          <TableCell>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSendTest(application.id!, application);
              }}
              disabled={isProcessing || !canResend}
              className={`
                inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium
                transition-all duration-200
                ${
                  isProcessing || !canResend
                    ? "bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                    : "bg-brand-500 hover:bg-brand-600 text-white active:scale-95"
                }
              `}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Send Test</span>
                </>
              )}
            </button>
          </TableCell>
        );
      },
    }),
  ];

  const handleRowClick = (application: JobApplicationType) => {
    if (application.id) {
      navigate(`/careers/job-application/${application.id}`);
    }
  };

  return (
    <div className="space-y-4">
      {rejectionModal.isOpen && (
        <RejectionConfirmationModal
          onClose={handleRejectionCancel}
          onConfirm={handleRejectionConfirm}
          applicantName={rejectionModal.applicantName}
          jobTitle={rejectionModal.jobTitle}
        />
      )}
      <Card extra="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
            Filters
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Filter job applications by status, job opening, or date range
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <SelectField
              options={JOB_STATUS_OPTIONS}
              value={selectedStatus}
              onChange={handleStatusChange}
              extra="!w-full"
              label="Filter by Status"
            />
          </div>
          <div>
            <SelectField
              options={jobOpeningOptions}
              value={selectedJobOpening}
              onChange={handleJobOpeningChange}
              extra="!w-full"
              label="Filter by Job Opening"
            />
          </div>
          <div>
            <label className="block text-sm text-navy-700 dark:text-white font-bold mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 p-3 text-sm outline-none dark:text-white focus:ring-2 focus:ring-brand-500"
              placeholder="Start Date"
            />
          </div>
          <div>
            <label className="block text-sm text-navy-700 dark:text-white font-bold mb-2">
              End Date
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 p-3 text-sm outline-none dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="End Date"
              />
              {(startDate || endDate) && (
                <button
                  onClick={handleClearDateFilter}
                  className="h-12 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors text-sm font-medium whitespace-nowrap"
                  title="Clear date filter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm text-navy-700 dark:text-white font-bold mb-2">
              First Test Score
            </label>
            <div className="flex items-center gap-2">
              <select
                value={testScoreOperator}
                onChange={handleTestScoreOperatorChange}
                className="flex h-12 items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 px-3 pr-8 text-sm outline-none dark:text-white focus:ring-2 focus:ring-brand-500 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                }}
              >
                <option value=">">{">"}</option>
                <option value=">=">{">="}</option>
                <option value="<">{"<"}</option>
                <option value="<=">{"<="}</option>
                <option value="=">{"="}</option>
              </select>
              <input
                type="text"
                value={testScoreValue}
                onChange={handleTestScoreValueChange}
                className="flex h-12 w-full items-center justify-center rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 p-3 text-sm outline-none dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="Score"
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {testScoreValue && (
                <button
                  onClick={handleClearTestScoreFilter}
                  className="h-12 px-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-navy-700 transition-colors text-sm font-medium whitespace-nowrap"
                  title="Clear test score filter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </Card>
      <Table<JobApplicationType>
        tableData={filteredApplications}
        columns={columns}
        tableHeading="Job Applications"
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default JobApplicationsTable;
